import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, PlayCircle, Award, GraduationCap, ArrowRight, RefreshCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function MyLearning() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["my-learning"], queryFn: () => api.get("/me/enrollments").then((r) => r.data) });

  const cancelSub = async (c) => {
    if (!window.confirm(`Cancel your subscription to "${c.title}"? You keep access until the current billing period ends, and no further charges will be made.`)) return;
    try {
      const r = await api.post(`/courses/${c.course_id}/subscription/cancel`);
      toast.success(r.data.ends ? `Subscription cancelled — access until ${r.data.ends}` : "Subscription cancelled");
      qc.invalidateQueries({ queryKey: ["my-learning"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 min-h-[70vh]" data-testid="my-learning">
      <p className="eyw-label mb-1">Your journey</p>
      <h1 className="font-serif text-4xl text-[#1A242B] flex items-center gap-3"><GraduationCap className="text-[#D98E73]" /> My Learning</h1>
      <p className="text-[#4A5D6B] mt-2 mb-8">Continue where you left off, and collect certificates as you complete courses.</p>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#144F75]" size={30} /></div>
      ) : (data || []).length === 0 ? (
        <div className="eyw-card p-10 text-center">
          <p className="text-[#4A5D6B]">You haven't enrolled in any courses yet.</p>
          <Link to="/courses" className="eyw-btn mt-4 inline-flex">Browse courses <ArrowRight size={16} /></Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {data.map((c) => (
            <div key={c.course_id} className="eyw-card overflow-hidden flex flex-col" data-testid={`learning-${c.course_id}`}>
              <div className="aspect-video bg-[#E5E0D8] relative">
                {c.thumbnail ? <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" /> :
                  <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#144F75] to-[#0d3550]"><PlayCircle className="text-white/70" size={36} /></div>}
                {c.progress_pct >= 100 && <span className="absolute top-3 right-3 bg-[#5C8A72] text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1"><Award size={12} /> Completed</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <p className="eyw-label mb-0.5">{c.category}</p>
                <h3 className="font-serif text-lg text-[#1A242B]">{c.title}</h3>
                <p className="text-xs text-[#8FA89B] mt-0.5">by {c.enhancer_name} · {c.completed}/{c.lesson_count} lessons</p>
                {c.is_subscription && (
                  <p className="text-xs mt-1.5 flex items-center gap-1.5" data-testid={`sub-info-${c.course_id}`}>
                    <RefreshCcw size={11} className="text-[#144F75]" />
                    {c.subscription_status === "cancel_at_period_end" || c.subscription_status === "canceled"
                      ? <span className="text-[#B5794E]">Subscription cancelled{c.subscription_ends ? ` — access until ${c.subscription_ends}` : ""}</span>
                      : <span className="text-[#4A5D6B]">Subscription · {c.sub_label}</span>}
                  </p>
                )}
                <div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden mt-3 mb-4">
                  <span className="block h-full bg-[#5C8A72] transition-all" style={{ width: `${c.progress_pct}%` }} />
                </div>
                <div className="flex gap-2 mt-auto">
                  <Link to={`/courses/${c.course_id}`} className="eyw-btn !py-2 text-sm flex-1 justify-center">{c.progress_pct > 0 ? "Continue" : "Start"} <ArrowRight size={15} /></Link>
                  {c.progress_pct >= 100 && <Link to={`/courses/${c.course_id}/certificate`} data-testid={`cert-link-${c.course_id}`} className="eyw-btn-outline !py-2 text-sm"><Award size={15} /> Certificate</Link>}
                </div>
                {c.is_subscription && c.subscription_status !== "cancel_at_period_end" && c.subscription_status !== "canceled" && (
                  <button onClick={() => cancelSub(c)} data-testid={`cancel-sub-${c.course_id}`}
                    className="text-xs text-[#8FA89B] hover:text-red-500 mt-2.5 inline-flex items-center gap-1 self-start transition-colors">
                    <XCircle size={12} /> Cancel subscription
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
